import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, Filter } from "lucide-react";
import dayjs from "dayjs";
import LineChartComponent from "./LineChartComponent";
import {
  getDeviceHealthHistoryById,
  getDeviceStatusChangeHistoryById,
  getSensorAgentVersionHistoryById,
} from "../services/apiServices";
import {
  DeviceStatusChange,
  SensorAgentVersionChange,
  DeviceHealthHistory,
} from "@schemas";

const HISTORY_CATEGORIES = {
  deviceHealth: "Device Health History",
  keyChange: "Token Change History",
  sensorAgentVersion: "Sensor Agent Version History",
  deviceStatus: "Device Status History",
};

const FILTER_CATEGORIES = {
  last7days: { label: "Last 7 Days", key: "last7days" },
  last1month: { label: "Last 1 month", key: "last1month" },
};

const DeviceHistory: React.FC<{ deviceId?: string }> = ({ deviceId }) => {
  const [activeHistory, setActiveHistory] = useState<string | null>(
    HISTORY_CATEGORIES.deviceHealth
  );
  const [showFilterDropDown, setShowFilterDropDown] = useState<boolean>(false);
  const [filter, setFilter] =
    useState<keyof typeof FILTER_CATEGORIES>("last7days");
  const [deviceTableData, setDeviceTableData] = useState<DeviceHealthHistory[]>(
    []
  );
  const [sensorAgentVersionData, setSensorAgentVersionData] = useState<
    SensorAgentVersionChange[]
  >([]);
  const [deviceStatusChangeData, setDeviceStatusChangeData] = useState<
    DeviceStatusChange[]
  >([]);

  const handleFilterSelection = (key: keyof typeof FILTER_CATEGORIES) => {
    setFilter(key);
    setShowFilterDropDown(false);
  };

  const filterData = (data: DeviceHealthHistory[]) => {
    const currentDate = new Date();
    let filteredData = [...data];

    if (filter === FILTER_CATEGORIES.last7days.key) {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(currentDate.getDate() - 7);
      filteredData = data.filter(
        (device) => new Date(device.heartbeat_timestamp) > sevenDaysAgo
      );
    } else if (filter === FILTER_CATEGORIES.last1month.key) {
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(currentDate.getMonth() - 1);
      filteredData = data.filter(
        (device) => new Date(device.heartbeat_timestamp) > oneMonthAgo
      );
    }

    return filteredData;
  };

  // const generateChartData = (data: DeviceHealthHistory[]) => {
  //   const grouped: { [key: string]: number } = {};

  //   data.forEach((entry) => {
  //     const timestamp = new Date(entry.heartbeat_timestamp);
  //     let key = "";

  //     if (filter === "last7days") {
  //       key = timestamp.toLocaleDateString("en-US", {
  //         month: "short",
  //         day: "numeric",
  //       }); // e.g. "Apr 26"
  //     } else if (filter === "last1month") {
  //       const today = new Date();
  //       const monthStart = new Date(
  //         today.getFullYear(),
  //         today.getMonth() - 1,
  //         today.getDate()
  //       );
  //       const diffDays = Math.floor((+timestamp - +monthStart) / 86400000);
  //       const weekIndex = Math.floor(diffDays / 7);
  //       key = `Week ${weekIndex + 1}`;
  //     }

  //     if (!grouped[key]) grouped[key] = 0;
  //     grouped[key]++;
  //   });

  //   const keys = Object.keys(grouped).sort(
  //     (a, b) => new Date(a).getTime() - new Date(b).getTime()
  //   );

  //   return keys.map((key) => {
  //     const count = grouped[key];
  //     const expected = filter === "last7days" ? 144 : 1008;
  //     const percentage = Math.round((count / expected) * 100);

  //     return {
  //       name: key,
  //       percentage,
  //     };
  //   });
  // };

  const generateChartData = (data: DeviceHealthHistory[]) => {
    const grouped: Record<string, number> = {};
    const today = dayjs();
    const expectedPerDay = 144; // 10 min intervals
    const expectedPerWeek = expectedPerDay * 7;

    if (filter === "last7days") {
      // Initialize last 7 days
      for (let i = 6; i >= 0; i--) {
        const date = today.subtract(i, "day").format("MMM D");
        grouped[date] = 0;
      }

      data.forEach(({ heartbeat_timestamp }) => {
        const key = dayjs(heartbeat_timestamp).format("MMM D");
        if (key in grouped) grouped[key]++;
      });

      return Object.entries(grouped).map(([name, count]) => ({
        name,
        percentage: Math.round((count / expectedPerDay) * 100),
      }));
    }

    if (filter === "last1month") {
      const start = today.subtract(30, "day");
      const weeks = Array.from({ length: 5 }, (_, i) => {
        const weekStart = start.add(i * 7, "day");
        const label = `Week ${i + 1}`;
        grouped[label] = 0;
        return { label, start: weekStart, end: weekStart.add(7, "day") };
      });

      data.forEach(({ heartbeat_timestamp }) => {
        const ts = dayjs(heartbeat_timestamp);
        const week = weeks.find(
          (w) =>
            ts.isSame(w.start, "day") ||
            (ts.isAfter(w.start) && ts.isBefore(w.end))
        );
        if (week) grouped[week.label]++;
      });

      return weeks.map(({ label }) => ({
        name: label,
        percentage: Math.round((grouped[label] / expectedPerWeek) * 100),
      }));
    }

    return [];
  };

  // Device Health History
  const fetchDeviceHealthHistory = async () => {
    try {
      // const response = await fetchDeviceHealth();
      // console.log(response.data);
      if (deviceId) {
        const response = await getDeviceHealthHistoryById(deviceId);
        console.log(response);
        setDeviceTableData(response.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // generateChartData(
  //   filterData(deviceTableData)
  // ).map((d) => ({
  //   name: d.name,
  //   noOfItems: d.percentage,
  //   value: `${d.percentage}%`,
  //   color:
  //     d.percentage >= 95
  //       ? "custom.norisk"
  //       : d.percentage >= 80
  //       ? "custom.risklow"
  //       : d.percentage >= 60
  //       ? "custom.riskmoderate"
  //       : d.percentage >= 40
  //       ? "custom.riskhigh"
  //       : "custom.riskcritical",
  // }))

  // Key Change History
  const fetchKeyChangeHistory = async () => {};

  // Sensor Agent Version History
  const fetchSensorAgentVersionHistory = async () => {
    try {
      if (deviceId) {
        const response = await getSensorAgentVersionHistoryById(deviceId);
        setSensorAgentVersionData(response.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Device Status Change History
  const fetchDeviceStatusChangeHistory = async () => {
    try {
      if (deviceId) {
        const response = await getDeviceStatusChangeHistoryById(deviceId);
        setDeviceStatusChangeData(response.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchDeviceHealthHistory();
    fetchSensorAgentVersionHistory();
    fetchDeviceStatusChangeHistory();
  }, []);

  const renderDeviceHealthHistory = () => {
    console.log(deviceTableData);
    const filteredData = filterData(deviceTableData);
    console.log(filteredData);

    return (
      <div className="flex gap-[10px] rounded-lg w-full min-h-[275px]">
        <div className="w-1/2 overflow-auto flex-grow custom-scrollbar bg-white rounded-lg shadow-md">
          <table
            id="data-table"
            className="min-w-full bg-white border rounded-lg"
          >
            <thead className="sticky top-0 z-10 bg-custom-blue0 h-20">
              <tr className="bg-custom-blue0 text-sm text-left text-custom-blue7">
                <th className="p-2">S. No</th>
                <th className="p-2">Device ID</th>
                <th className="p-2">Heart Beat Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                <>
                  {filteredData.map((device, index) => (
                    <tr
                      key={index}
                      className={`${
                        index % 2 === 1 ? "bg-custom-blue0" : ""
                      } hover:bg-custom-blue0 text-sm text-custom-blue7`}
                    >
                      <td className="p-2">{index + 1}</td>
                      <td className="p-2">{device.device_id}</td>
                      <td className="p-2">{device.heartbeat_timestamp}</td>
                    </tr>
                  ))}
                </>
              ) : (
                <tr>
                  <td colSpan={3} className="text-center">
                    Data Not Found!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="w-1/2 bg-white rounded-lg overflow-hidden shadow-md">
          {/* <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{ top: 15, right: 30, left: -25, bottom: 0 }}
            >
              <CartesianGrid stroke="#f5f5f5" />
              <XAxis dataKey="x" type="number" />
              <YAxis />
              <Line
                type="monotone"
                dataKey="y"
                stroke="#002B5B"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer> */}
          <LineChartComponent
            title=""
            footer=""
            absoluteYearsData={generateChartData(
              filterData(deviceTableData)
            ).map((d) => ({
              name: d.name,
              noOfItems: d.percentage,
              value: `${d.percentage}%`,
              color:
                d.percentage >= 95
                  ? "custom.norisk"
                  : d.percentage >= 80
                  ? "custom.risklow"
                  : d.percentage >= 60
                  ? "custom.riskmoderate"
                  : d.percentage >= 40
                  ? "custom.riskhigh"
                  : "custom.riskcritical",
            }))}
          />
        </div>
      </div>
    );
  };

  // [
  //   {
  //     name: "2025",
  //     noOfItems: 10,
  //     color: "custom.norisk",
  //     value: "Low",
  //   },
  //   {
  //     name: "2026",
  //     noOfItems: 20,
  //     color: "custom.riskverylow",
  //     value: "Moderate",
  //   },
  //   {
  //     name: "2027",
  //     noOfItems: 35,
  //     color: "custom.risklow",
  //     value: "Elevated",
  //   },
  //   {
  //     name: "2028",
  //     noOfItems: 50,
  //     color: "custom.riskmoderate",
  //     value: "Serious",
  //   },
  //   {
  //     name: "2029",
  //     noOfItems: 65,
  //     color: "custom.riskhigh",
  //     value: "High",
  //   },
  //   {
  //     name: "2030",
  //     noOfItems: 80,
  //     color: "custom.riskveryhigh",
  //     value: "Severe",
  //   },
  //   {
  //     name: "2031",
  //     noOfItems: 100,
  //     color: "custom.riskcritical",
  //     value: "Critical",
  //   },
  // ]

  const renderKeyChangeHistory = () => {
    return (
      <div className="rounded-lg w-full min-h-[275px]">
        <div className="w-full overflow-auto flex-grow bg-white custom-scrollbar rounded-lg h-[275px]">
          <table
            id="data-table"
            className="min-w-full bg-white shadow border rounded-lg"
          >
            <thead className="sticky top-0 z-10 bg-custom-blue0 h-20">
              <tr className="bg-custom-blue0 text-sm text-left text-custom-blue7">
                <th className="p-2">S. No</th>
                <th className="p-2">Timestamp</th>
                <th className="p-2">Changed By</th>
              </tr>
            </thead>
            <tbody>
              <tr
                className={`${
                  0 % 2 === 1 ? "bg-custom-blue0" : ""
                } hover:bg-custom-blue0 text-sm text-custom-blue7`}
              >
                <td className="p-2">1</td>
                <td className="p-2">2025-04-17 12:30:00</td>
                <td className="p-2">User</td>
              </tr>
              <tr
                className={`${
                  1 % 2 === 1 ? "bg-custom-blue0" : ""
                } hover:bg-custom-blue0 text-sm text-custom-blue7`}
              >
                <td className="p-2">2</td>
                <td className="p-2">2023-10-01 12:00:00</td>
                <td className="p-2">Admin</td>
              </tr>
              <tr
                className={`${
                  2 % 2 === 1 ? "bg-custom-blue0" : ""
                } hover:bg-custom-blue0 text-sm text-custom-blue7`}
              >
                <td className="p-2">3</td>
                <td className="p-2">2023-10-01 12:00:00</td>
                <td className="p-2">Admin</td>
              </tr>
              <tr
                className={`${
                  3 % 2 === 1 ? "bg-custom-blue0" : ""
                } hover:bg-custom-blue0 text-sm text-custom-blue7`}
              >
                <td className="p-2">4</td>
                <td className="p-2">2023-10-01 12:00:00</td>
                <td className="p-2">User</td>
              </tr>
              <tr
                className={`${
                  4 % 2 === 1 ? "bg-custom-blue0" : ""
                } hover:bg-custom-blue0 text-sm text-custom-blue7`}
              >
                <td className="p-2">5</td>
                <td className="p-2">2023-10-01 12:00:00</td>
                <td className="p-2">Admin</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderSensorAgentVersionHistory = () => {
    return (
      <div className="rounded-lg w-full min-h-[275px] overflow-auto">
        <div className="w-full flex-grow custom-scrollbar bg-white rounded-lg h-[275px]">
          <table
            id="data-table"
            className="min-w-full bg-white shadow border rounded-lg"
          >
            <thead className="sticky top-0 z-10 bg-custom-blue0 h-20">
              <tr className="bg-custom-blue0 text-sm text-left text-custom-blue7">
                <th className="p-2">S. No</th>
                <th className="p-2">Sensor Agent Version</th>
                <th className="p-2">Timestamp</th>
                <th className="p-2">Changed By</th>
              </tr>
            </thead>
            <tbody>
              {sensorAgentVersionData.length > 0 ? (
                <>
                  {sensorAgentVersionData.map(
                    (data: SensorAgentVersionChange, index) => (
                      <tr
                        key={index}
                        className={`${
                          index % 2 === 1 ? "bg-custom-blue0" : ""
                        } hover:bg-custom-blue0 text-sm text-custom-blue7`}
                      >
                        <td className="p-2">{index + 1}</td>
                        <td className="p-2">{data.sensor_agent_version}</td>
                        <td className="p-2">{data.changed_at}</td>
                        <td className="p-2">{data.changed_by}</td>
                      </tr>
                    )
                  )}
                </>
              ) : (
                <tr>
                  <td colSpan={4} className="text-center">
                    No Data Found!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderDeviceStatusHistory = () => {
    return (
      <div className="rounded-lg w-full min-h-[275px] overflow-auto">
        <div className="w-full flex-grow custom-scrollbar bg-white rounded-lg h-[275px]">
          <table
            id="data-table"
            className="min-w-full bg-white shadow border rounded-lg"
          >
            <thead className="sticky top-0 z-10 bg-custom-blue0 h-20">
              <tr className="bg-custom-blue0 text-sm text-left text-custom-blue7">
                <th className="p-2">S. No</th>
                <th className="p-2">Device Scan Status</th>
                <th className="p-2">Timestamp</th>
                <th className="p-2">Changed By</th>
              </tr>
            </thead>
            <tbody>
              {deviceStatusChangeData.length > 0 ? (
                <>
                  {deviceStatusChangeData.map(
                    (data: DeviceStatusChange, index) => (
                      <tr
                        key={data.id}
                        className={`${
                          index % 2 === 1 ? "bg-custom-blue0" : ""
                        } hover:bg-custom-blue0 text-sm text-custom-blue7`}
                      >
                        <td className="p-2">{index + 1}</td>
                        <td className="p-2">{data.action}</td>
                        <td className="p-2">{data.changed_at}</td>
                        <td className="p-2">{data.changed_by}</td>
                      </tr>
                    )
                  )}
                </>
              ) : (
                <tr>
                  <td colSpan={4} className="text-center">
                    No Data Found!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };
  return (
    <div className="flex flex-col gap-3 w-full overflow-auto bg-custom-lightbg rounded-[10px] py-[15px] px-[10px]">
      {/* Device Health History */}
      <div
        id={HISTORY_CATEGORIES.deviceHealth}
        className={`${
          activeHistory === HISTORY_CATEGORIES.deviceHealth
            ? "bg-custom-blue7 text-white"
            : "bg-white text-custom-bluenew"
        } h-[61px] rounded-[5px] p-[10px] flex items-center justify-between shadow-custom`}
      >
        <h1 className="text-xl font-bold">Device Health History</h1>
        <div className="flex gap-4 relative">
          {activeHistory === HISTORY_CATEGORIES.deviceHealth && (
            <button
              onClick={() => setShowFilterDropDown(!showFilterDropDown)}
              className="h-8 w-8 rounded-full bg-white flex items-center justify-center"
            >
              <Filter className="text-custom-bluenew" size={17} />
            </button>
          )}
          {showFilterDropDown && (
            <div className="absolute z-10 bg-white shadow-md text-custom-bluenew rounded-md p-2 right-8 top-8 w-[130px]">
              {Object.keys(FILTER_CATEGORIES).map((key) => (
                <button
                  key={key}
                  type="button"
                  className="block w-full text-left p-2 text-sm hover:bg-gray-100 rounded-md"
                  onClick={() =>
                    handleFilterSelection(key as keyof typeof FILTER_CATEGORIES)
                  }
                >
                  {
                    FILTER_CATEGORIES[key as keyof typeof FILTER_CATEGORIES]
                      .label
                  }
                </button>
              ))}
            </div>
          )}
          <button
            onClick={() => {
              setActiveHistory((prev) =>
                prev === HISTORY_CATEGORIES.deviceHealth
                  ? null
                  : HISTORY_CATEGORIES.deviceHealth
              );
            }}
            className={`h-8 w-8 rounded-full ${
              activeHistory === HISTORY_CATEGORIES.deviceHealth
                ? "bg-white"
                : "bg-custom-bluenew"
            } flex items-center justify-center`}
          >
            {activeHistory === HISTORY_CATEGORIES.deviceHealth ? (
              <ChevronUp
                strokeWidth={3}
                className="text-custom-bluenew"
                size={17}
              />
            ) : (
              <ChevronDown strokeWidth={3} className="text-white" size={17} />
            )}
          </button>
        </div>
      </div>

      {activeHistory === HISTORY_CATEGORIES.deviceHealth &&
        renderDeviceHealthHistory()}

      {/* Key Change History */}
      <div
        id={HISTORY_CATEGORIES.keyChange}
        className={`${
          activeHistory === HISTORY_CATEGORIES.keyChange
            ? "bg-custom-blue7 text-white"
            : "bg-white text-custom-bluenew"
        } h-[61px] rounded-[5px] p-[10px] flex items-center justify-between shadow-custom`}
      >
        <h1 className="text-xl font-bold ">Token Rotation Histroy</h1>
        <button
          onClick={() => {
            setActiveHistory((prev) =>
              prev === HISTORY_CATEGORIES.keyChange
                ? null
                : HISTORY_CATEGORIES.keyChange
            );
          }}
          className={`h-8 w-8 rounded-full ${
            activeHistory === HISTORY_CATEGORIES.keyChange
              ? "bg-white"
              : "bg-custom-bluenew"
          } flex items-center justify-center`}
        >
          {activeHistory === HISTORY_CATEGORIES.keyChange ? (
            <ChevronUp
              strokeWidth={3}
              className="text-custom-bluenew"
              size={17}
            />
          ) : (
            <ChevronDown strokeWidth={3} className="text-white" size={17} />
          )}
        </button>
      </div>

      {activeHistory === HISTORY_CATEGORIES.keyChange &&
        renderKeyChangeHistory()}

      {/* Sensor Agent Version History */}
      <div
        id={HISTORY_CATEGORIES.sensorAgentVersion}
        className={`${
          activeHistory === HISTORY_CATEGORIES.sensorAgentVersion
            ? "bg-custom-blue7 text-white"
            : "bg-white text-custom-bluenew"
        } h-[61px] rounded-[5px] p-[10px] flex items-center justify-between shadow-custom`}
      >
        <h1 className="text-xl font-bold">Sensor Agent Version History</h1>
        <button
          onClick={() => {
            setActiveHistory((prev) =>
              prev === HISTORY_CATEGORIES.sensorAgentVersion
                ? null
                : HISTORY_CATEGORIES.sensorAgentVersion
            );
          }}
          className={`h-8 w-8 rounded-full ${
            activeHistory === HISTORY_CATEGORIES.sensorAgentVersion
              ? "bg-white"
              : "bg-custom-bluenew"
          } flex items-center justify-center`}
        >
          {activeHistory === HISTORY_CATEGORIES.sensorAgentVersion ? (
            <ChevronUp
              strokeWidth={3}
              className="text-custom-bluenew"
              size={17}
            />
          ) : (
            <ChevronDown strokeWidth={3} className="text-white" size={17} />
          )}
        </button>
      </div>

      {activeHistory === HISTORY_CATEGORIES.sensorAgentVersion &&
        renderSensorAgentVersionHistory()}

      {/* Device Status History */}
      <div
        id={HISTORY_CATEGORIES.deviceStatus}
        className={`${
          activeHistory === HISTORY_CATEGORIES.deviceStatus
            ? "bg-custom-blue7 text-white"
            : "bg-white text-custom-bluenew"
        } h-[61px] rounded-[5px] p-[10px] flex items-center justify-between shadow-custom`}
      >
        <h1 className="text-xl font-bold">Device Status History</h1>
        <button
          onClick={() => {
            setActiveHistory((prev) =>
              prev === HISTORY_CATEGORIES.deviceStatus
                ? null
                : HISTORY_CATEGORIES.deviceStatus
            );
          }}
          className={`h-8 w-8 rounded-full ${
            activeHistory === HISTORY_CATEGORIES.deviceStatus
              ? "bg-white"
              : "bg-custom-bluenew"
          } flex items-center justify-center`}
        >
          {activeHistory === HISTORY_CATEGORIES.deviceStatus ? (
            <ChevronUp
              strokeWidth={3}
              className="text-custom-bluenew"
              size={17}
            />
          ) : (
            <ChevronDown strokeWidth={3} className="text-white" size={17} />
          )}
        </button>
      </div>

      {activeHistory === HISTORY_CATEGORIES.deviceStatus &&
        renderDeviceStatusHistory()}
    </div>
  );
};

export default DeviceHistory;
